import BucketedFacetSerializer from './serializers/BucketedFacetSerializer'
import NumericRangeFacetSerializer from './serializers/NumericRangeFacetSerializer'
import {
  DisplayFacetFragment,
  NumericRangeDisplayFacetFragment
} from '../../fragments/Facets.generated'
import { DepartmentNavigationLinkFragment } from '../../components/Header/NavHierarchyFragments.generated'
import { FacetSelectionPayloadFragment } from '#src/fragments/FacetSelectionPayload.generated'
import { FacetSelection, SellableSearchFilter } from '../../generated/types'
import { FacetSerializer } from './types'
import numeral from 'numeral'
import uniq from 'lodash/uniq'
import without from 'lodash/without'
import cloneDeep from 'lodash/cloneDeep'
import fromPairs from 'lodash/fromPairs'
import repeat from 'lodash/repeat'
import chunk from 'lodash/chunk'
import trim from 'lodash/trim'
import omit from 'lodash/omit'
import logger from '../logger'
import { SellableSort } from '#src/generated/types'
import { SearchEntryPoint } from '@deal/web-tracking/types/client/events/consumer/types'

const FACET_SERIALIZERS: FacetSerializer[] = [BucketedFacetSerializer, NumericRangeFacetSerializer]

export interface AliasedFacetSelection extends FacetSelection {
  alias: string | null
}

export type SearchQueryFacetSelection = AliasedFacetSelection | FacetSelectionPayloadFragment

export type SearchQuerySelections = {
  [facetName: string]: SearchQueryFacetSelection
}

export interface SearchQuery {
  categorySlug: string
  keywords?: string
  // TODO: Switch from a dict to an array?
  selections?: SearchQuerySelections
  filters?: SellableSearchFilter[]
  // TODO:  Remove when srp v2 is ramped
  after?: string
  before?: string
  // v2 pagination
  page?: string
  sortBy?: SellableSort
  // for tracking
  entryPoint?: SearchEntryPoint
}

export interface SearchLocation {
  facetPathname?: string
  pathname: string
  searchParams: URLSearchParams
}

/**
 * Serializes a `SearchQuery` into a format that can be used as a URL:
 *
 *  - The category is serialized as the URL path
 *  - The keywords, search type, and pagination cursors are serialized as top-level query params
 *  - The facets are serialized as query params with prefixes denoting their facet type.
 *      (each facet type determines its own serialization format)
 */
export function serializeSearchQuery(query: SearchQuery): SearchLocation {
  if (!query.categorySlug) {
    throw new Error('Can not serialize search query without a category slug')
  }

  const pathSegments = []
  const searchParams = new URLSearchParams()

  if (query.page) {
    searchParams.append('page', query.page)
  }

  if (query.keywords) {
    searchParams.append('q', query.keywords)
  }

  if (query.after) {
    searchParams.append('after', query.after)
  }

  if (query.before) {
    searchParams.append('before', query.before)
  }

  if (query.selections) {
    const sortedSelections = Object.values(query.selections).sort((a, b) => {
      const keyA = a.alias || a.name
      const keyB = b.alias || b.name

      // Sort by length of key, then lexigraphically
      if (keyA.length !== keyB.length) {
        return keyA.length - keyB.length
      } else {
        return keyA.localeCompare(keyB)
      }
    })

    for (let facetSelection of sortedSelections) {
      // For each selection, find the appropriate serializer for that facet selection type,
      //   based on the `isSerializable` type guard implementations.
      const serializer = FACET_SERIALIZERS.find(serializer => {
        return serializer.isSerializable(facetSelection)
      })

      if (!serializer) {
        throw new Error(
          `Could not determine the facet selection type for the "${facetSelection.name}" facet`
        )
      }

      const name = facetSelection.name
      const alias = facetSelection.alias
      const value = serializer.serialize(facetSelection)
      const prefix = serializer.SERIALIZATION_PREFIX

      if (value) {
        const selectionCount = facetSelection.bucketValues ? facetSelection.bucketValues.length : 0

        if (alias && selectionCount === 1) {
          pathSegments.push(`${alias}/${encodeURIComponent(value)}`)
        } else {
          searchParams.append(`${prefix}:${alias || name}`, value)
        }
      }
    }
  }

  if (query.filters) {
    let filters = query.filters.slice()

    const isDealsSearch = filters.includes(SellableSearchFilter.DISCOUNTED)

    if (isDealsSearch) {
      // Remove the "DISCOUNTED" as that filter is implied by the "deals" suffix
      filters = without(filters, SellableSearchFilter.DISCOUNTED)
      pathSegments.push('deals')
    }

    // Encode any remaining filters as the `filters` query param
    if (filters.length) {
      searchParams.append('filters', filters.map(filter => filter.toLowerCase()).join(','))
    }
  }

  if (query.sortBy) {
    switch (query.sortBy) {
      case SellableSort.AVG_SALE_PRICE:
        searchParams.append('sort', 'min-price')
        break

      case SellableSort.MAX_PRICE_SAVINGS:
        searchParams.append('sort', 'max-dollars-off')
        break

      case SellableSort.MAX_PRICE_PERCENT_SAVINGS:
        searchParams.append('sort', 'max-percent-off')
        break

      case SellableSort.BEST_SELLER:
        searchParams.append('sort', 'most-purchased')
        break

      case SellableSort.POPULARITY:
        searchParams.append('sort', 'most-viewed')
        break
    }
  }

  if (query.entryPoint) {
    searchParams.append('source', query.entryPoint)
  }

  let facetPathname
  let pathname = '/c/' + query.categorySlug
  if (pathSegments.length) {
    facetPathname = '/' + pathSegments.join('/')
    pathname += facetPathname
  }

  return {
    facetPathname,
    pathname,
    searchParams
  }
}

/**
 * Deserialize a URL (path and query string) to a `SearchQuery`
 *
 * See the comment for `serializeSearchQuery` for details on the serialization format.
 */
export function deserializeSearchQuery({ pathname, searchParams }: SearchLocation): SearchQuery {
  // Deserialize the path portion of the URL:
  //  1) Remove the special "/deals" suffix
  //  2) Trim leading and trailing forward slashes    => key-1/val-1/key-2/val-2
  //  3) Split by remaining forward slashes           => [key-1, val-1, key-2, val-2]
  //  4) Chunk it into groups of 2                    => [[key-1, val-1], [key-2, val-2]]
  //  5) Convert it to a dictionary                   => { key-1: val-1, key-2: val-2}
  const isDealsSearch = pathname.endsWith('/deals')
  pathname = pathname.replace(/\/deals$/, '')
  const deserializedPathSegments = fromPairs(chunk(trim(pathname, '/').split('/'), 2))
  const categorySlug = deserializedPathSegments['c']
  const facetSelectionKV = omit(deserializedPathSegments, 'c')

  const query: SearchQuery = {
    categorySlug
  }

  for (let [facetAlias, facetValue] of Object.entries(facetSelectionKV)) {
    if (!query.selections) {
      query.selections = {}
    }

    query.selections[facetAlias] = {
      name: facetAlias,
      alias: facetAlias,
      bucketValues: [decodeURIComponent(facetValue)]
    }
  }

  // Handle deal search
  if (isDealsSearch) {
    query.filters = [SellableSearchFilter.DISCOUNTED]
  }

  // @ts-ignore TypeScript has incomplete typings for URLSearchParams
  // See: https://github.com/Microsoft/TSJS-lib-generator/pull/167#issuecomment-263764497
  for (var [key, value] of searchParams.entries()) {
    switch (key) {
      case 'q':
        query.keywords = value
        break

      case 'after':
        query.after = value
        break

      case 'before':
        query.before = value
        break

      case 'page':
        query.page = value
        break

      case 'sort':
        switch (value) {
          case 'min-price':
            query.sortBy = SellableSort.AVG_SALE_PRICE
            break

          case 'max-dollars-off':
            query.sortBy = SellableSort.MAX_PRICE_SAVINGS
            break

          case 'max-percent-off':
            query.sortBy = SellableSort.MAX_PRICE_PERCENT_SAVINGS
            break

          case 'most-purchased':
            query.sortBy = SellableSort.BEST_SELLER
            break

          case 'most-viewed':
            query.sortBy = SellableSort.POPULARITY
            break

          default:
            query.sortBy = SellableSort.RELEVANCE
        }
        break

      case 'filters':
        query.filters = query.filters || []

        value.split(',').forEach((filterValue: string) => {
          const filterEnumValue = filterValue.toUpperCase()

          if (filterEnumValue in SellableSearchFilter) {
            query.filters?.push(
              SellableSearchFilter[(filterEnumValue as unknown) as SellableSearchFilter]
            )
          } else {
            logger.captureException(new Error(`Unknown filter in search URL: ${filterEnumValue}`))
          }
        })
        break

      case 'source':
        switch (value) {
          case 'department-page-subcategory-tiles':
          case 'nav-browse':
          case 'nav-keywords':
          case 'pdp-breadcrumbs':
          case 'srp-breadcrumbs':
          case 'srp-category-dropdown':
          case 'srp-facets':
          case 'srp-featured-facets':
          case 'srp-keywords':
          case 'srp-pagination':
          case 'srp-sort':
          case 'srp-subcategory-tiles':
            query.entryPoint = value
            break

          default:
            break
        }

      default:
        const prefixedQueryParam: string = key

        // For each selection, find the appropriate serializer for that facet selection type
        //   based on the facet's prefix.
        FACET_SERIALIZERS.some(serializer => {
          const prefix = serializer.SERIALIZATION_PREFIX

          if (prefixedQueryParam.startsWith(prefix + ':')) {
            if (!query.selections) {
              query.selections = {}
            }

            const facetName = prefixedQueryParam.slice(prefix.length + 1)

            query.selections[facetName] = {
              name: facetName,
              alias: null,
              ...serializer.deserialize(value)
            }

            return true
          }

          return false
        })
    }
  }

  query.filters = uniq(query.filters)

  return query
}

/**
 * "Select" a bucket value for a bucketed facet given an existing SearchQuery
 */
export function addBucketSelectionToSearchQuery(
  existingQuery: SearchQuery,
  displayFacet: DisplayFacetFragment,
  value: string
) {
  const newQuery = cloneDeep(existingQuery)
  const existingSelection = cloneDeep(existingQuery.selections || {})[
    displayFacet.alias || displayFacet.name
  ]
  const existingBuckets = new Set(existingSelection ? existingSelection.bucketValues : [])

  existingBuckets.add(value)

  if (!newQuery.selections) {
    newQuery.selections = {}
  }

  newQuery.selections[displayFacet.alias || displayFacet.name] = {
    name: displayFacet.name,
    alias: displayFacet.alias,
    bucketValues: Array.from(existingBuckets)
  } as AliasedFacetSelection

  return newQuery
}

/**
 * Safely convert a SearchQuery into an array of FacetSelections, suitable to use as GraphQL input
 */
export function searchQuerySelectionsToFacetSelections(
  selections: SearchQuerySelections
): FacetSelection[] {
  return Object.values(cloneDeep(selections)).map(selection => {
    delete selection.alias
    return selection
  })
}

/**
 * Format a single selection value for a NumericRangeDisplayFacet in a format suitable
 *   for use as a label (i.e. abbreviated)
 */
export function formatNumericFacetValueAsLabel(
  value: number,
  facet: NumericRangeDisplayFacetFragment
) {
  const scale = facet.scale
  const upper = parseInt(facet.upper, 10)
  const isAboveUpperCap = value >= upper && facet.upperCapped
  const suffix = isAboveUpperCap ? '+' : ''

  switch (facet.type) {
    case 'USD_CURRENCY':
      // Use abbreviations for values of more than $10,000
      return numeral(value / 100).format(value / 100 >= 10000 ? '($0a)' : '$0,0') + suffix

    default:
      // Scale the stored to it's actual representation, and include that many decimals
      const format = scale > 0 ? `0,0.${repeat('0', scale)}` : '0,0'
      return numeral(value * Math.pow(10, -scale)).format(format) + suffix
  }
}

/**
 * Format a FacetSelection using `formatNumericFacetValueAsLabel`
 */
export function formatNumericFacetSelectionAsLabel(
  selection: FacetSelection,
  facet: NumericRangeDisplayFacetFragment
) {
  const lowerLabel = selection.lower && formatNumericFacetValueAsLabel(selection.lower, facet)
  const upperLabel = selection.upper && formatNumericFacetValueAsLabel(selection.upper, facet)

  if (lowerLabel && upperLabel) {
    return `${lowerLabel} - ${upperLabel}`
  } else if (lowerLabel) {
    return `${lowerLabel}+`
  } else if (upperLabel) {
    return `Up to ${upperLabel}`
  } else {
    return 'Any'
  }
}

export const getVisiblePages = (pageNumber: number, totalPages: number) => {
  if (totalPages < 7) {
    return [1, 2, 3, 4, 5, 6].filter(pageNumber => pageNumber <= totalPages)
  } else if (pageNumber % 5 >= 0 && pageNumber > 4 && pageNumber + 2 < totalPages) {
    return [1, pageNumber - 1, pageNumber, pageNumber + 1, totalPages]
  } else if (pageNumber % 5 >= 0 && pageNumber > 4 && pageNumber + 2 >= totalPages) {
    return [1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  } else {
    return [1, 2, 3, 4, 5, totalPages]
  }
}

export const getSellableSearchResultsCountLabel = (totalCount: number, maximumExceeded: boolean) =>
  maximumExceeded
    ? '10,000+ results'
    : `${numeral(totalCount).format('0,0')} result${totalCount > 1 ? 's' : ''}`

export const getQueryParamsFromDeptNavLink = (deptNavLink: DepartmentNavigationLinkFragment) => {
  switch (deptNavLink.__typename) {
    case 'DepartmentNavigationCategoryLink':
      return { categorySlug: deptNavLink.category.slug }

    case 'DepartmentNavigationSearchLink':
      return { categorySlug: deptNavLink.category.slug, facets: deptNavLink.facets }

    case 'DepartmentNavigationDealsLink':
      return { categorySlug: deptNavLink.category.slug, filters: [SellableSearchFilter.DISCOUNTED] }

    default:
      return null
  }
}
